/** @jsx jsx */
import { jsx, css } from '@compiled/react';
import type { IntlShape } from 'react-intl-next';
import { defineMessages, useIntl } from 'react-intl-next';

import { token } from '@atlaskit/tokens';
import { TrelloIcon } from '@atlaskit/logo';
import { Text } from '@atlaskit/primitives/compiled';
import type {
	ExtensionConfiguration,
	SelectionExtensionPlugin,
} from '@atlaskit/editor-plugin-selection-extension';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';

import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer';
import { createTrelloCardHandle } from '@confluence/highlight-actions/entry-points/createTrelloCardHandle';
import { trelloI18nMessages } from '@confluence/highlight-actions/entry-points/trelloI18nMessages';

const messages = defineMessages({
	menuItemLabel: {
		id: 'editor-features.trello-inbox.create-card',
		defaultMessage: 'Send to your Trello Inbox',
		description: 'Create a Trello card from highlighted text',
	},
});

const iconWrapperStyles = css({
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	height: token('space.200'),
	width: token('space.200'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Needed to size TrelloIcon to match other toolbar icons
	'& svg': {
		height: token('space.200'),
		width: token('space.200'),
	},
});

const handleCreateTrelloCard = async (
	contentId: string,
	flags: FlagsStateContainer | undefined,
	formatMessage: IntlShape['formatMessage'],
) => {
	if (!flags) {
		return;
	}

	try {
		const selection = window.getSelection();
		if (!selection || selection.rangeCount === 0) {
			return;
		}
		const selectedText = selection.toString().trim();
		if (!selectedText) {
			return;
		}
		const result = await createTrelloCardHandle({
			text: selectedText,
			contentId,
		});
		if (result?.cardUrl) {
			const titleNode = <Text>{formatMessage(trelloI18nMessages.successTitle)}</Text>;
			void flags.showFlag({
				id: `trello-inbox-create-success-${Date.now()}`,
				title: titleNode,
				appearance: 'success',
				actions: [
					{
						content: formatMessage(trelloI18nMessages.openCard),
						href: result.cardUrl!,
						target: '_blank',
					},
				],
			});
		} else {
			void flags.showFlag({
				id: `trello-inbox-create-error-${Date.now()}`,
				title: formatMessage(trelloI18nMessages.errorTitle),
				appearance: 'error',
			});
		}
	} catch {
		void flags.showFlag({
			id: `trello-inbox-create-error-${Date.now()}`,
			title: formatMessage(trelloI18nMessages.errorTitle),
			appearance: 'error',
		});
	}
};

export const useTrelloInboxSelectionExtension = (
	contentId: string | undefined,
	_editorAPIRef: React.RefObject<PublicPluginAPI<[SelectionExtensionPlugin]> | null | undefined>,
	flags?: FlagsStateContainer,
): ExtensionConfiguration => {
	const { formatMessage } = useIntl();

	return {
		key: 'trello-inbox',
		source: 'first-party',
		inlineToolbar: {
			getMenuItems: () => {
				const label = formatMessage(messages.menuItemLabel);
				const icon = () => (
					<span css={iconWrapperStyles}>
						<TrelloIcon label={label} appearance="neutral" size="small" />
					</span>
				);

				return [
					{
						label,
						icon,
						onClick: () => handleCreateTrelloCard(contentId || '', flags, formatMessage),
					},
				];
			},
		},
	};
};
